import React, { useEffect, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import { batchList, batchStudentsMarksheetData } from "../../../api/Batch";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import StudentHeader from "../../students/v2/StudentHeader";

export default function MarksheetReport() {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({});

  const getBatchList = async () => {
    try {
      const res = await batchList();
      if (res.data.status) setBatches(res.data.data);
    } catch (err) {
      SweetAlertShow("error", "Error", err.message);
    }
  };

  // ✅ Marksheet API
  const getMarksheet = async () => {
    if (!filters?.batch_id) return;

    setLoading(true);
    try {
      const res = await batchStudentsMarksheetData(
        `batch_id=${filters.batch_id}`
      );
      if (res.data.status) {
        setStudents(res.data.data.active_batch_students);
      }
    } catch (err) {
      SweetAlertShow("error", "Error", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBatchList();
  }, [filters?.course_id]);

  useEffect(() => {
    getMarksheet();
  }, [filters?.batch_id]);

  // ✅ REPORT GENERATE
  const generateReport = () => {
    let report = [];

    students.forEach((student) => {

      const name = `${student?.student_info?.name?.toUpperCase()} (${student?.student_info?.father_name?.toUpperCase()})`;
     
      student?.student_info?.series?.forEach((series) => {
        series?.tests?.forEach((test) => {
          let row = {
            student: name,
            series: series.series_name,
            test: test.test_name,
            total: Number(test.total_marks) || 0,
            obtained: Number(test?.marks?.obtained_total) || 0,
            rank: test?.marks?.rank ?? null,
          };

          test?.subjects_detail?.forEach((sub) => {
            row[sub.name] = isNaN(sub.obtained_marks)
              ? sub.obtained_marks
              : Number(sub.obtained_marks);
          });

          report.push(row);
        });
      });
    });

    return report;
  };

  const reportData = generateReport();

  // ✅ Group by student
  const groupedData = {};
  reportData.forEach((row) => {
    if (!groupedData[row.student]) {
      groupedData[row.student] = [];
    }
    groupedData[row.student].push(row);
  });

  // ✅ Subjects
  const subjects =
    students?.[0]?.student_info?.series?.[0]?.tests?.[0]?.subjects_detail?.map(
      (s) => s.name
    ) || [];

  // Rank Calculation
  const rankedStudents = Object.keys(groupedData).map((student) => {
    let total = 0;
    let obtained = 0;

    groupedData[student].forEach((r) => {
      total += r.total;
      obtained += r.obtained;
    });

    return {
      name: student,
      percent: total ? (obtained / total) * 100 : 0,
    };
  });

  rankedStudents.sort((a, b) => b.percent - a.percent);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="container-fluid mt-2">
        <div className="row">
          <div className="col-md-12">
            {/* FILTER */}
            <div className="card mb-3">
              <div className="card-body row">
                <div className="col-md-4">
                  <label>Batch</label>
                  <select
                    className="form-control"
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        batch_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Batch</option>
                    {batches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* TABLE */}
            <div className="card">
              <div className="card-body">
                <h5 className="text-primary">Marksheet Report</h5>

                <div className="table-responsive">
                  <table className="table table-bordered table-sm text-center">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Series</th>
                        <th>Test</th>

                        {subjects.map((sub, i) => (
                          <th key={i}>{sub}</th>
                        ))}

                        <th>Total</th>
                        <th>Obtained</th>
                        <th>%</th>
                        <th>Rank</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Object.keys(groupedData).length > 0 ? (
                        Object.keys(groupedData).map((studentName, i) => {
                          const rows = groupedData[studentName];

                          let grandTotal = 0;
                          let grandObtained = 0;

                          return (
                            <>
                              {rows.map((row, index) => {
                                grandTotal += row.total;
                                grandObtained += row.obtained;

                                return (
                                  <tr key={i + "-" + index}>
                                    {index === 0 && (
                                      <td
                                        rowSpan={rows.length + 1}
                                        className="fw-bold align-middle"
                                      >
                                        {studentName}
                                      </td>
                                    )}

                                    <td>{row.series}</td>
                                    <td>{row.test}</td>

                                    {subjects.map((sub, j) => (
                                      <td key={j}>{row[sub] ?? "-"}</td>
                                    ))}

                                    <td>{row.total}</td>
                                    <td>{row.obtained}</td>
                                    <td>
                                      {row.total
                                        ? (
                                            (row.obtained / row.total) *
                                            100
                                          ).toFixed(2)
                                        : 0}
                                      %
                                    </td>
                                  </tr>
                                );
                              })}

                              <tr className="table-success fw-bold">
                                <td colSpan={2}>Total</td>

                                {subjects.map((sub, j) => {
                                  let subTotal = 0;
                                  rows.forEach((r) => {
                                    subTotal += Number(r[sub]) || 0;
                                  });
                                  return <td key={j}>{subTotal}</td>;
                                })}

                                <td>{grandTotal}</td>
                                <td>{grandObtained}</td>
                                
                                <td>
                                  {grandTotal
                                    ? (
                                        (grandObtained / grandTotal) *
                                        100
                                      ).toFixed(2)
                                    : 0}
                                  %
                                </td>
                                <td>
    {rankedStudents.findIndex(s => s.name === studentName) + 1}
  </td>
                              </tr>
                            </>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={7 + subjects.length}>No Data Found</td>
                        </tr>
                      )}
                      
                    </tbody>
                  </table>
                </div>

                {/* Optional CSS */}
                <style>
                  {`
                          td {
                            vertical-align: middle !important;
                          }
                        `}
                </style>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
